import React, { useMemo, useState } from 'react';
import { ScrollView } from 'react-native';
import styled from 'styled-components/native';

import { BubbleLines } from '~/components/bubble-lines';
import { Button } from '~/components/button';
import { Color } from '~/components/color';
import { Markdown } from '~/components/markdown';
import { Spacing } from '~/components/spacing';
import { Task, TaskColorScheme } from '~/components/task';
import { Text } from '~/components/text';
import { useIntl } from '~/contexts/intl';

export type InteractionTheme = 'blue' | 'pink';

type InteractionThemeType = {
    bubbleBgColor: Color;
    textColor: Color;
    labelScheme: TaskColorScheme;
    linesColor: Color;
};

const interactionThemes: { [key in InteractionTheme]: InteractionThemeType } = {
    blue: {
        bubbleBgColor: Color.TASK_DEFAULT,
        textColor: Color.TEXT_TERTIARY,
        labelScheme: 'active',
        linesColor: Color.BACKGROUND_TERTIARY
    },
    pink: {
        bubbleBgColor: Color.BACKGROUND_TERTIARY,
        textColor: Color.TEXT_DEFAULT,
        labelScheme: 'active',
        linesColor: Color.CHAT_BADGE
    }
};

type InteractionMessageProps = {
    label?: string;
    title?: string;
    description?: string;
    descriptionMarkdown?: string;
    expandableDescription?: boolean;
    maxDescriptionChars?: number;
    actionLabel: string;
    theme: InteractionTheme;
    onPress: () => Promise<void>;
};

const InteractionMessageContainer = styled.View<{ backgroundColor: string }>`
    background-color: ${({ backgroundColor }) => backgroundColor};
    flex-direction: column;
    border-radius: 16px;
    align-items: flex-start;
    padding: 16px;
    flex: 1 1 auto;
`;

const LinesContainer = styled.View`
    position: absolute;
    width: 200px;
    height: 24px;
    bottom: 0px;
    right: 0px;
`;

const ButtonContainer = styled.View`
    width: 60%;
    margin-top: ${Spacing.MEDIUM}px;
`;

const MAX_DESCRIPTION_CHARACTERS = 120;
const MAX_EXPANDED_DESCRIPTION_HEIGHT = 300;

export const InteractionMessage = (props: InteractionMessageProps) => {
    const {
        label,
        title,
        description,
        descriptionMarkdown,
        expandableDescription = false,
        maxDescriptionChars = MAX_DESCRIPTION_CHARACTERS,
        actionLabel,
        onPress,
        theme
    } = props;

    const { formatMessage } = useIntl();
    const messageTheme = interactionThemes[theme];

    const [isExpanded, setIsExpanded] = useState(false);

    const { displayText, shouldTruncate, hasContent } = useMemo(() => {
        const full = descriptionMarkdown ?? description ?? '';
        const should = expandableDescription && full.length > maxDescriptionChars && !isExpanded;
        const text = should ? `${full.slice(0, maxDescriptionChars)}...` : full;
        return {
            displayText: text,
            shouldTruncate: expandableDescription && full.length > maxDescriptionChars,
            hasContent: full.length > 0
        };
    }, [descriptionMarkdown, description, expandableDescription, maxDescriptionChars, isExpanded]);

    return (
        <InteractionMessageContainer backgroundColor={messageTheme.bubbleBgColor}>
            <LinesContainer>
                <BubbleLines fill={messageTheme.linesColor} />
            </LinesContainer>
            {label && <Task text={label} scheme={messageTheme.labelScheme} />}
            {title && (
                <Text.HEADER_3 style={{ color: messageTheme.textColor }} before={Spacing.SMALL} after={Spacing.SMALL}>
                    {title}
                </Text.HEADER_3>
            )}

            {hasContent ? (
                <>
                    <ScrollView
                        style={{ maxHeight: MAX_EXPANDED_DESCRIPTION_HEIGHT, alignSelf: 'stretch' }}
                        showsVerticalScrollIndicator
                        horizontal={false}
                    >
                        <Markdown
                            styles={{
                                body: { ...Text.PARAGRAPH_2_STYLES, color: messageTheme.textColor },
                                heading1: { ...Text.SUBTITLE_1_STYLES, color: Color.SMOOTHIE },
                                heading2: { ...Text.PARAGRAPH_1_STYLES, marginTop: 8 },
                                heading3: Text.SUBTITLE_1_STYLES,
                                strong: Text.PARAGRAPH_2_STYLES
                            }}
                        >
                            {displayText}
                        </Markdown>
                    </ScrollView>
                    {shouldTruncate ? (
                        <Button
                            type="text"
                            size="small"
                            alignCenter={false}
                            label={isExpanded ? formatMessage('showLess') : formatMessage('showMore')}
                            onPress={() => setIsExpanded(prev => !prev)}
                            style={{ marginTop: Spacing.SMALL }}
                        />
                    ) : null}
                </>
            ) : null}

            <ButtonContainer>
                <Button label={actionLabel} shape="rounded" size="regular" fillContainer onPress={onPress} />
            </ButtonContainer>
        </InteractionMessageContainer>
    );
};
